/**
 * Which baby's tab is active. Remembered per device so reopening the app at
 * 3am lands on the twin you were logging; validated against the live baby list
 * so a renamed or deleted baby never leaves a dangling selection. A tapped
 * bottle alert opens `/?baby=<id>`, which wins over the remembered one.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { alertFromSearch } from "./push-state.ts";
import { useBabies } from "./queries.ts";

const STORAGE_KEY = "babia.selectedBaby";

interface SelectedBabyValue {
  babyId: string | null;
  setBabyId: (id: string) => void;
}

const Ctx = createContext<SelectedBabyValue>({ babyId: null, setBabyId: () => {} });

export function SelectedBabyProvider({ children }: { children: React.ReactNode }) {
  const babies = useBabies();
  const [stored, setStored] = useState<string | null>(() => {
    const fromAlert = alertFromSearch(window.location.search).babyId;
    if (fromAlert) return fromAlert;
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const list = babies.data ?? [];
  const babyId = list.some((b) => b.id === stored) ? stored : (list[0]?.id ?? null);

  useEffect(() => {
    if (!babyId) return;
    try {
      localStorage.setItem(STORAGE_KEY, babyId);
    } catch {
      // ignore
    }
  }, [babyId]);

  const setBabyId = useCallback((id: string) => setStored(id), []);
  const value = useMemo(() => ({ babyId, setBabyId }), [babyId, setBabyId]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSelectedBaby(): SelectedBabyValue {
  return useContext(Ctx);
}
